import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GetVariantPickerForPlp } from 'autogen/swagger/all';
import { OpenVariantPicker } from 'autogen/translation-keys/trans-website-shared-buttons';
import { ButtonWithSpinner } from 'components/Button';
import Icon from 'components/Icon';
import { PowerstepMediatorAtom } from 'components/powerstep/shared/hooks/usePowerstep';
import useRegimeQuickView from 'components/product/regime/components/RegimeQuickView/components/RegimeQuickViewButton/hooks/useRegimeQuickView';
import VariantPicker from 'components/product/Variants/VariantPicker';
import useVariantPickerQuickView from 'components/product/Variants/VariantPicker/shared/hooks/useVariantPickerQuickView';
import { useSlides } from 'components/quickView';
import isClient from 'helpers/isClient';
import useMediatorState from 'hooks/globals/useMediatorAtom';
import { eventCategoryToContext, GA4Tracking } from 'modules/tracking/GA4';
import type { IProductActionTrackingContext, ProductActionProps } from '../../shared/types';

function track(trackingContext: IProductActionTrackingContext) {
    if (!isClient) return;

    const { eventCategory } = trackingContext;
    const context = eventCategoryToContext(eventCategory);

    GA4Tracking({
        eventName: 'open_variant_picker',
        context,
        category: 'ecommerce'
    });
}

function useOpenVariantPickerCallback({ params }: ProductActionProps['VariantPicker']) {
    const { productId, trackingContext } = params;
    const [, toggle] = useVariantPickerQuickView();
    const [, setPowerstepMediatorState] = useMediatorState(PowerstepMediatorAtom);
    const [{ isOpen: quickViewIsOpen }] = useRegimeQuickView();
    const { push } = useSlides();

    const onClickHandler = useCallback(async () => {
        await GetVariantPickerForPlp({ id: productId }).then((res) => {
            if (quickViewIsOpen) {
                push(() => <VariantPicker model={res} />);
            } else {
                toggle({
                    isOpen: true,
                    variantPickerModel: res
                });
            }
        });

        // close powerstep
        setPowerstepMediatorState({ isOpen: false, productIds: [productId] });
        track(trackingContext);
    }, [quickViewIsOpen, push, toggle, setPowerstepMediatorState, trackingContext, productId]);

    return onClickHandler;
}

export function VariantPickerButton(params: ProductActionProps['VariantPicker']): JSX.Element {
    const { ...restProps } = params;
    const { t } = useTranslation();
    const onClickHandler = useOpenVariantPickerCallback(params);

    return (
        <ButtonWithSpinner type="button" onClick={onClickHandler} {...restProps}>
            {t(OpenVariantPicker)}
        </ButtonWithSpinner>
    );
}

export function VariantPickerButtonCompact(params: ProductActionProps['VariantPicker']): JSX.Element {
    const { t } = useTranslation();
    const { ...restProps } = params;
    const onClickHandler = useOpenVariantPickerCallback(params);

    return (
        <ButtonWithSpinner
            size="md"
            type="button"
            onClick={onClickHandler}
            aria-label={t(OpenVariantPicker)}
            {...restProps}
        >
            <Icon type="BasketPlus" size={16} fill="currentColor" />
        </ButtonWithSpinner>
    );
}

export default Object.assign(VariantPickerButton, {
    Compact: VariantPickerButtonCompact
});
